/**
 * Convertit une couleur hex + opacité en chaîne rgba CSS.
 */
const hexToRgba = (hex, opacity) => {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16) || 0
  const g = parseInt(h.slice(2, 4), 16) || 0
  const b = parseInt(h.slice(4, 6), 16) || 0
  return `rgba(${r},${g},${b},${opacity})`
}

/**
 * Résout une valeur d'arrière-plan en propriétés CSS inline.
 * Compatible avec les anciens formats (string hex ou URL) et le nouveau format objet.
 *
 * Formats acceptés :
 *   { type: 'color', color: '#rrggbb' }
 *   { type: 'image', image: 'https://...', overlay?: { color, opacity } }
 *   '#rrggbb'         (legacy couleur)
 *   'https://...'     (legacy image)
 *
 * @returns {object} Style React inline (backgroundImage, backgroundColor, etc.)
 */
export const resolveBackground = (value) => {
  if (!value) return {}

  // Nouveau format objet
  if (typeof value === 'object') {
    if (value.type === 'color') {
      return { backgroundColor: value.color || 'transparent' }
    }
    if (value.type === 'gradient' && value.gradient?.stops?.length >= 2) {
      const sorted = [...value.gradient.stops].sort((a, b) => a.position - b.position)
      const stops = sorted.map(s => `${s.color} ${s.position}%`).join(', ')
      return { backgroundImage: `linear-gradient(${value.gradient.angle ?? 135}deg, ${stops})` }
    }
    if (value.type === 'image' && value.image) {
      const imageLayer = `url(${value.image})`
      const hasOverlay = value.overlay?.color && value.overlay?.opacity > 0
      return {
        backgroundImage: hasOverlay
          ? `linear-gradient(${hexToRgba(value.overlay.color, value.overlay.opacity)}, ${hexToRgba(value.overlay.color, value.overlay.opacity)}), ${imageLayer}`
          : imageLayer,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    }
    return {}
  }

  // Legacy : string couleur (#rrggbb) ou URL image
  if (typeof value === 'string') {
    if (value.startsWith('#') || value.startsWith('rgb')) {
      return { backgroundColor: value }
    }
    if (value.startsWith('http') || value.startsWith('/') || value.startsWith('data:')) {
      return {
        backgroundImage: `url(${value})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    }
  }

  return {}
}

/**
 * Résout une valeur de lien en { href, target }.
 * Compatible avec l'ancien format string et le nouveau format objet { url, target }.
 */
export const resolveLink = (value) => {
  if (!value) return { href: '#', target: undefined, rel: undefined }
  const raw = typeof value === 'string'
    ? { url: value, target: '' }
    : { url: value.url ?? '', target: value.target ?? '' }

  const href = raw.url || '#'
  const target = raw.target === '_blank' ? '_blank' : undefined
  const rel = target ? 'noopener noreferrer' : undefined

  return { href, target, rel }
}
